// ManagementPage.jsx
//
// Owner-facing admin hub. Three tabs, all rendered inside one page:
//   1. تعيين الأقساط    — set tuition / bus fees per academic year
//   2. إدارة الحسابات   — list users + add + change password + delete
//   3. النسخ الاحتياطي  — backup download + restore upload
//
// Auth: tabs 2 and 3 are owner-only; tab 1 is owner OR accountant.

import React, { useState, useEffect, useRef } from "react";
import * as api from "../services/api";
import Card from "../components/Card";
import Modal from "../components/Modal";
import Button from "../components/Button";
import Input from "../components/Input";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../hooks/useAuth";
import {
  ArchiveBoxIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CurrencyDollarIcon,
  KeyIcon,
  PlusCircleIcon,
  TrashIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { UserRole } from "../types";

/* =====================================================================
 * Constants & helpers
 * ===================================================================== */

const SCHOOL_STAGES = [
  { key: "الابتدائي", label: "المرحلة الابتدائية" },
  { key: "الإعدادي", label: "المرحلة الإعدادية" },
  { key: "الثانوي", label: "المرحلة الثانوية" },
];

const roleLabels = {
  [UserRole.OWNER]: "مدير النظام",
  [UserRole.SECRETARY]: "أمانة السر",
  [UserRole.ACCOUNTANT]: "محاسب",
};

const roleBadgeClasses = {
  [UserRole.OWNER]: "bg-purple-100 text-purple-700 border-purple-200",
  [UserRole.SECRETARY]: "bg-emerald-100 text-emerald-700 border-emerald-200",
  [UserRole.ACCOUNTANT]: "bg-blue-100 text-blue-700 border-blue-200",
};

const generateAcademicYears = (start = 2025, end = 2035) => {
  const years = [];
  for (let y = start; y <= end; y++) years.push(`${y}-${y + 1}`);
  return years;
};
const ACADEMIC_YEARS = generateAcademicYears(2025, 2035);

const containerVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.18 } },
};

/* =====================================================================
 * Tab 1 — تعيين الأقساط (polished alignment)
 * ===================================================================== */
const FeesTab = () => {
  const [fees, setFees] = useState({
    الابتدائي: { tuition: "", busFee: "" },
    الإعدادي: { tuition: "", busFee: "" },
    الثانوي: { tuition: "", busFee: "" },
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [academicYear, setAcademicYear] = useState(ACADEMIC_YEARS[0]);
  const [yearHasData, setYearHasData] = useState(false);
  const lastFetchedRef = useRef(null);

  const normalize = (obj) => ({
    الابتدائي: {
      tuition: obj?.الابتدائي?.tuition ?? "",
      busFee: obj?.الابتدائي?.busFee ?? "",
    },
    الإعدادي: {
      tuition: obj?.الإعدادي?.tuition ?? "",
      busFee: obj?.الإعدادي?.busFee ?? "",
    },
    الثانوي: {
      tuition: obj?.الثانوي?.tuition ?? "",
      busFee: obj?.الثانوي?.busFee ?? "",
    },
  });

  const isDirty = () => {
    const last = lastFetchedRef.current;
    if (!last) {
      return Object.values(fees).some((s) => s.tuition !== "" || s.busFee !== "");
    }
    const norm = normalize(last);
    return SCHOOL_STAGES.some(
      ({ key }) =>
        String(fees[key].tuition) !== String(norm[key].tuition) ||
        String(fees[key].busFee) !== String(norm[key].busFee)
    );
  };

  const fetchFeesForYear = async (year) => {
    setFetching(true);
    try {
      const res = await api.getFees?.(year);
      if (!res) {
        setYearHasData(false);
        lastFetchedRef.current = null;
        setFees(normalize({}));
        toast.info("لا توجد بيانات رسوم مُهيّأة لهذه السنة.");
      } else {
        setYearHasData(true);
        lastFetchedRef.current = res;
        setFees(normalize(res));
      }
    } catch (err) {
      setYearHasData(false);
      lastFetchedRef.current = null;
      setFees(normalize({}));
      toast.error(err?.response?.data?.error || "خطأ أثناء جلب بيانات الرسوم.");
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchFeesForYear(academicYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAcademicYearChange = async (e) => {
    const newYear = e.target.value;
    if (isDirty()) {
      const ok = window.confirm("هناك تغييرات غير محفوظة. هل تريد المتابعة؟");
      if (!ok) return;
    }
    setAcademicYear(newYear);
    await fetchFeesForYear(newYear);
  };

  const handleStageChange = (stage, field, value) => {
    const numeric = value === "" ? "" : Number(value);
    setFees((prev) => ({
      ...prev,
      [stage]: { ...prev[stage], [field]: numeric },
    }));
  };

  const handleSubmitFees = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        academicYear,
        الابتدائي: {
          tuition: Number(fees.الابتدائي.tuition) || 0,
          busFee: Number(fees.الابتدائي.busFee) || 0,
        },
        الإعدادي: {
          tuition: Number(fees.الإعدادي.tuition) || 0,
          busFee: Number(fees.الإعدادي.busFee) || 0,
        },
        الثانوي: {
          tuition: Number(fees.الثانوي.tuition) || 0,
          busFee: Number(fees.الثانوي.busFee) || 0,
        },
      };
      await api.setFees(payload);
      toast.success("تم حفظ الرسوم بنجاح.");
      await fetchFeesForYear(academicYear);
    } catch (err) {
      toast.error(err?.response?.data?.error || "فشل في حفظ الرسوم.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-5xl mx-auto"
    >
      <Card title="تحديد رسوم المراحل الدراسية">
        <form onSubmit={handleSubmitFees} className="space-y-6">
          {/* Year selector */}
          <div className="flex flex-wrap items-end gap-4 pb-4 border-b border-slate-100">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                السنة الدراسية
              </label>
              <select
                value={academicYear}
                onChange={handleAcademicYearChange}
                disabled={fetching || loading}
                className="block w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-200"
              >
                {ACADEMIC_YEARS.map((y) => (
                  <option key={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="text-sm px-3 py-2 rounded-full border bg-slate-50">
              {fetching ? (
                <span className="text-slate-600">جارٍ التحقق...</span>
              ) : yearHasData ? (
                <span className="text-emerald-700 font-medium">مهيأ</span>
              ) : (
                <span className="text-rose-700 font-medium">غير مهيأ</span>
              )}
            </div>
          </div>

          {/* Stage rows — 3-column grid: label / tuition / bus fee */}
          <div className="space-y-3">
            <div className="hidden md:grid md:grid-cols-[1fr_2fr_2fr] gap-4 text-sm font-semibold text-slate-500 px-1">
              <div>المرحلة</div>
              <div>رسوم الدراسة (ل.س)</div>
              <div>رسوم النقل (ل.س)</div>
            </div>
            {SCHOOL_STAGES.map(({ key, label }) => (
              <div
                key={key}
                className="grid grid-cols-1 md:grid-cols-[1fr_2fr_2fr] gap-3 md:gap-4 items-center p-3 rounded-xl bg-slate-50/60 border border-slate-100"
              >
                <div className="font-semibold text-slate-800 md:px-1">
                  {label}
                </div>
                <Input
                  type="number"
                  value={fees[key].tuition}
                  onChange={(e) => handleStageChange(key, "tuition", e.target.value)}
                  required
                  min="0"
                  placeholder="رسوم الدراسة"
                />
                <Input
                  type="number"
                  value={fees[key].busFee}
                  onChange={(e) => handleStageChange(key, "busFee", e.target.value)}
                  required
                  min="0"
                  placeholder="رسوم النقل"
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 !px-8"
            >
              {loading ? "جاري الحفظ..." : "حفظ الرسوم"}
            </Button>
          </div>
        </form>
      </Card>
    </motion.div>
  );
};

/* =====================================================================
 * Tab 2 — إدارة الحسابات
 * ===================================================================== */

const AddUserModal = ({ isOpen, onClose, onCreated }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(UserRole.SECRETARY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setUsername("");
      setPassword("");
      setRole(UserRole.SECRETARY);
    }
  }, [isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.addUser({ username, password, role });
      toast.success(`تم إنشاء المستخدم: ${username}`);
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل في إنشاء المستخدم");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="إضافة مستخدم جديد">
      <form onSubmit={submit} className="space-y-4">
        <Input
          label="اسم المستخدم"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
        <Input
          label="كلمة المرور (8 أحرف على الأقل)"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
        />
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            الدور
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-200"
          >
            {Object.values(UserRole).map((r) => (
              <option key={r} value={r}>
                {roleLabels[r] || r}
              </option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            إلغاء
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {loading ? "جاري الإنشاء..." : "إنشاء المستخدم"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const ChangePasswordModal = ({ isOpen, onClose, account, onSaved }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setConfirm("");
    }
  }, [isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("كلمة المرور يجب أن تكون 8 أحرف على الأقل");
      return;
    }
    if (password !== confirm) {
      toast.error("كلمتا المرور غير متطابقتين");
      return;
    }
    setLoading(true);
    try {
      await api.changeUserPassword(account._id ?? account.id, password);
      toast.success("تم تحديث كلمة المرور بنجاح");
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل في تحديث كلمة المرور");
    } finally {
      setLoading(false);
    }
  };

  if (!account) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`تغيير كلمة مرور: ${account.username}`}
    >
      <form onSubmit={submit} className="space-y-4">
        <Input
          label="كلمة المرور الجديدة"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
        />
        <Input
          label="تأكيد كلمة المرور"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
          minLength={8}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            إلغاء
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {loading ? "جاري الحفظ..." : "حفظ كلمة المرور"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const AccountsTab = () => {
  // The login response shape from the backend is
  // `{ token, role, user: { id, username, role } }`, so the integer id
  // for the currently-logged-in account lives at `me.user.id`, not `me.id`.
  // (Older code in the page assumed `me.id` and silently never matched —
  // the trash icon was never disabled for the current user. The backend
  // still rejects the delete via `req.user.id` either way, but the
  // frontend should grey the button out so the user isn't surprised.)
  const auth = useAuth();
  const myId = auth?.user?.user?.id ?? auth?.user?.id ?? null;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [pwdTarget, setPwdTarget] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.listUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل في جلب المستخدمين");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (account) => {
    if (!window.confirm(`تأكيد حذف المستخدم "${account.username}"؟`)) return;
    try {
      await api.deleteUser(account._id ?? account.id);
      toast.success("تم حذف المستخدم");
      fetchUsers();
    } catch (err) {
      toast.error(err?.response?.data?.message || "فشل في حذف المستخدم");
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-5xl mx-auto"
    >
      <Card title="إدارة الحسابات">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-slate-100">
          <p className="text-slate-600 text-sm">
            عرض حسابات المستخدمين، تعديل كلمات المرور، أو إنشاء حسابات جديدة.
          </p>
          <Button
            onClick={() => setAddOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 inline-flex items-center gap-2"
          >
            <PlusCircleIcon className="h-5 w-5" />
            مستخدم جديد
          </Button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-slate-500">جاري التحميل...</div>
        ) : users.length === 0 ? (
          <div className="py-10 text-center text-slate-500">لا يوجد مستخدمون.</div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="min-w-full text-xl">
              <thead>
                <tr className="text-right text-slate-500 text-base uppercase tracking-wide">
                  <th className="px-4 py-4 font-semibold">#</th>
                  <th className="px-4 py-4 font-semibold">اسم المستخدم</th>
                  <th className="px-4 py-4 font-semibold">الدور</th>
                  <th className="px-4 py-4 font-semibold text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u, idx) => {
                  const id = u._id ?? u.id;
                  const isMe = myId !== null && String(myId) === String(id);
                  return (
                    <tr key={id} className="hover:bg-slate-50/60">
                      <td className="px-4 py-4 text-slate-500">{idx + 1}</td>
                      <td className="px-4 py-4 font-semibold text-slate-800">
                        {u.username}
                        {isMe && (
                          <span className="mr-2 text-sm text-indigo-600 font-medium">
                            (أنت)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full border text-sm font-medium ${
                            roleBadgeClasses[u.role] ||
                            "bg-slate-100 text-slate-700 border-slate-200"
                          }`}
                        >
                          {roleLabels[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => setPwdTarget(u)}
                            title="تغيير كلمة المرور"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-amber-600 hover:bg-amber-50"
                          >
                            <KeyIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(u)}
                            disabled={isMe}
                            title={
                              isMe
                                ? "لا يمكنك حذف حسابك"
                                : "حذف المستخدم"
                            }
                            className={`inline-flex items-center justify-center h-9 w-9 rounded-lg ${
                              isMe
                                ? "text-slate-300 cursor-not-allowed"
                                : "text-rose-600 hover:bg-rose-50"
                            }`}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AddUserModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={fetchUsers}
      />
      <ChangePasswordModal
        isOpen={!!pwdTarget}
        onClose={() => setPwdTarget(null)}
        account={pwdTarget}
        onSaved={fetchUsers}
      />
    </motion.div>
  );
};

/* =====================================================================
 * Tab 3 — النسخ الاحتياطي
 * ===================================================================== */
const BackupTab = () => {
  const [downloading, setDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const handleBackup = async () => {
    setDownloading(true);
    toast.info("جاري إعداد النسخة الاحتياطية...", {
      autoClose: false,
      toastId: "backup",
    });
    try {
      const response = await api.backupDatabase();
      const blob = await response.blob();
      const filename = `backup-${new Date().toISOString().slice(0, 19)}.sql`;

      if (window.showSaveFilePicker) {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [
            {
              description: "MySQL backup",
              accept: { "application/sql": [".sql"] },
            },
          ],
        });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      }

      toast.dismiss("backup");
      toast.success("تم أخذ النسخة الاحتياطية بنجاح");
    } catch (err) {
      console.error(err);
      toast.dismiss("backup");
      toast.error("فشل في أخذ النسخة الاحتياطية");
    } finally {
      setDownloading(false);
    }
  };

  const handleRestore = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("يرجى اختيار ملف النسخة الاحتياطية");
    if (
      !window.confirm(
        "ستقوم عملية الاستعادة باستبدال البيانات الحالية. هل أنت متأكد؟"
      )
    ) {
      return;
    }
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append("backupFile", file);
      await api.restoreDatabase(formData);
      toast.success("تمت استعادة قاعدة البيانات بنجاح!");
      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.message || "فشل في عملية الاستعادة");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto"
    >
      <Card title="أخذ نسخة احتياطية">
        <div className="flex items-start gap-3 mb-4 pb-3 border-b border-slate-100">
          <ArrowDownTrayIcon className="h-6 w-6 text-rose-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600">
            تنزيل ملف SQL يحتوي على كامل بيانات قاعدة البيانات. يمكن استخدامه
            لاحقًا للاستعادة من قسم "استعادة من نسخة احتياطية".
          </p>
        </div>
        <Button
          onClick={handleBackup}
          disabled={downloading}
          className="w-full bg-rose-600 hover:bg-rose-700 inline-flex items-center justify-center gap-2"
        >
          <ArchiveBoxIcon className="h-5 w-5" />
          {downloading ? "جاري التحضير..." : "أخذ نسخة احتياطية الآن"}
        </Button>
      </Card>

      <Card title="استعادة من نسخة احتياطية">
        <div className="flex items-start gap-3 mb-4 pb-3 border-b border-slate-100">
          <ArrowUpTrayIcon className="h-6 w-6 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-sm text-slate-600">
            رفع ملف SQL سابق لاستعادة قاعدة البيانات. سيتم استبدال البيانات
            الحالية بالكامل.
          </p>
        </div>
        <form onSubmit={handleRestore} className="space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">
              ملف النسخة الاحتياطية (.sql)
            </span>
            <input
              type="file"
              accept=".sql"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </label>
          <Button
            type="submit"
            disabled={restoring || !file}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {restoring ? "جاري الاستعادة..." : "استعادة الآن"}
          </Button>
        </form>
      </Card>
    </motion.div>
  );
};

/* =====================================================================
 * Root page
 * ===================================================================== */
const ManagementPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const isOwner = role === UserRole.OWNER;
  const isAccountant = role === UserRole.ACCOUNTANT;
  const canAccess = isOwner || isAccountant;

  // Tabs available depend on role. Accountants only see the fees tab; the
  // page header / nav reflects that automatically.
  const tabs = [
    { id: "fees", label: "تعيين الأقساط", icon: CurrencyDollarIcon, show: canAccess },
    { id: "accounts", label: "إدارة الحسابات", icon: UserGroupIcon, show: isOwner },
    { id: "backup", label: "النسخ الاحتياطي", icon: ArchiveBoxIcon, show: isOwner },
  ].filter((t) => t.show);

  const [activeTab, setActiveTab] = useState(tabs[0]?.id || "fees");

  if (!canAccess) {
    return (
      <div
        dir="rtl"
        className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6 sm:p-10"
      >
        <div className="max-w-3xl mx-auto p-6 bg-amber-50 border-r-4 border-amber-400 rounded-xl shadow-md text-right">
          <p className="text-lg font-semibold text-amber-900">تنبيه صلاحيات</p>
          <p className="mt-2 text-amber-800">
            ليس لديك الصلاحيات للوصول إلى هذه الصفحة.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-8"
    >
      <div className="max-w-6xl mx-auto">
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 space-y-1"
        >
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-800">
            {isOwner ? "الإدارة العامة للنظام" : "إدارة الرسوم"}
          </h1>
          <p className="text-slate-600 text-base">
            {isOwner
              ? "لوحة التحكم لإدارة الرسوم، حسابات المستخدمين، والنسخ الاحتياطي."
              : "تحديد رسوم المراحل الدراسية لكل عام دراسي."}
          </p>
        </motion.header>

        {/* Tab nav — same visual language as AccountingPage's tabs.
            Hidden when only one tab is available (e.g. an accountant only
            sees the fees tab; rendering a single-button nav looks weird). */}
        <div className={`mb-8 ${tabs.length <= 1 ? "hidden" : ""}`}>
          <nav
            className="flex space-x-2 space-x-reverse p-1.5 bg-slate-200/70 rounded-xl overflow-x-auto"
            aria-label="Tabs"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative whitespace-nowrap flex-1 px-3 py-2.5 text-md font-semibold rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? "text-slate-900"
                      : "text-slate-800 hover:bg-white/60"
                  }`}
                >
                  {activeTab === tab.id && (
                    <motion.div
                      layoutId="active-mgmt-tab"
                      className="absolute inset-0 bg-white shadow-md rounded-lg"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <Icon className="h-5 w-5" />
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        <main>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.18 }}
            >
              {activeTab === "fees" && <FeesTab />}
              {activeTab === "accounts" && <AccountsTab />}
              {activeTab === "backup" && <BackupTab />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

export default ManagementPage;
